import React, { useCallback, useMemo, useRef } from 'react';
import {
  Autocomplete,
  AutocompleteOption,
} from '@/components/core/Autocomplete';
import { sanitizedParseFloat } from '@/helpers/math_helpers';
import IconButton from '@/components/core/IconButton';
import { IconTrash } from '@/icons';

type StringProps = {
  type: 'string';
  value: string[];
  onChange: (value: any[]) => void;
};

type NumberProps = {
  type: 'number';
  value: number[];
  onChange: (value: any[]) => void;
};

interface SuggestionProps {
  id: string;
  placeholder: string;
  options: AutocompleteOption[];
  maxVisibleOptions?: number;
  /** The element the suggestion list is kept inside of. */
  containerRef?: React.RefObject<HTMLElement>;
}

type MultiTextFieldProps = (StringProps | NumberProps) & SuggestionProps;

const MultiTextField: React.FC<MultiTextFieldProps> = ({
  id,
  value,
  onChange,
  type,
  placeholder,
  options,
  maxVisibleOptions,
  containerRef,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const values: (string | number)[] = value;

  const handleDelete = (i: number): void => {
    onChange([...value.slice(0, i), ...value.slice(i + 1)]);
  };

  const handleAdd = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return;
      }
      const added = type === 'number' ? sanitizedParseFloat(trimmed) : trimmed;
      if (typeof added === 'number' && !isFinite(added)) {
        return;
      }
      if (!values.includes(added)) {
        onChange([...values, added]);
      }
      ref.current?.blur();
      setTimeout(() => {
        ref.current?.focus();
      }, 1);
    },
    [onChange, type, values],
  );

  const remainingOptions = useMemo(
    () =>
      options.filter((op) => !values.some((v) => v.toString() === op.value)),
    [options, values],
  );

  return (
    <div className="flex flex-col gap-px">
      <div className="flex max-h-16 flex-wrap gap-1 overflow-y-auto">
        {values.map((item, i) => (
          <div
            key={`chip-${i}`}
            className="flex items-center gap-1 rounded-md bg-background-200 pl-2 text-sm text-text-950"
          >
            {item}
            <IconButton
              variant="ghost"
              color="red"
              size="sm"
              icon={<IconTrash />}
              onClick={() => handleDelete(i)}
            />
          </div>
        ))}
      </div>
      <Autocomplete
        id={id}
        ref={ref}
        options={remainingOptions}
        maxVisibleOptions={maxVisibleOptions}
        containerRef={containerRef}
        placeholder={placeholder}
        freeSolo
        // Each picked option and each typed entry becomes a chip, so the field
        // itself is kept empty and ready for the next value.
        value=""
        onValueChange={handleAdd}
      />
    </div>
  );
};

export default MultiTextField;
